
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings as SettingsIcon,
  ArrowLeft,
  Globe,
  Palette,
  Bell,
  Lock,
  Database,
  Mail,
  Save,
  RefreshCw,
  CheckCircle2,
  Image,
  Volume2,
  Monitor,
  Moon,
  Sun,
  Laptop,
  Clock,
  Key,
  FileImage,
  UploadCloud,
  BarChart,
  Eye,
  UserCheck,
  Languages,
  Calendar,
  Sparkles,
  Code,
  BellOff,
  ShieldCheck,
  HardDrive,
  Cloud,
  Zap,
  Ban,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../hooks/useSettings';
import { toast } from 'react-hot-toast'; 
import { scanExternalImageUrls, generateImageReport } from '../../utils/imageUrlScanner';
import { ImageBrowser } from '../../components/media/ImageBrowser';

const Settings = () => {
  const { settings, loading, imageUsage, updateSettings, resetSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');
  const [selectedImageKey, setSelectedImageKey] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ description: string } | null>(null);
  const [externalImages, setExternalImages] = useState<{ url: string; location: string; description: string; }[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadExternalImages();
  }, [refreshKey]);

  // Refresh images when category changes or settings update
  useEffect(() => {
    if (activeCategory === 'media') {
      loadExternalImages();
    }
  }, [activeCategory, settings, refreshKey]);

  const categories = [
    { id: 'general', label: 'Geral', icon: Globe },
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'security', label: 'Segurança', icon: Lock },
    { id: 'media', label: 'Media', icon: Image },
    { id: 'backup', label: 'Backup', icon: Database },
    { id: 'analytics', label: 'Análise', icon: BarChart }
  ];

  const handleSettingChange = (category: keyof AppSettings, setting: string, value: any) => {
    const newSettings = {
      ...settings,
      [category]: {
        ...settings[category],
        [setting]: value
      }
    };
    updateSettings(newSettings);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await resetSettings();
      toast.success('Configurações restauradas com sucesso!');
    } catch (error) {
      toast.error('Erro ao restaurar configurações');
    }
  };

  const handleImageBrowse = () => {
    setShowImageBrowser(true);
  };

  const getImageKeyFromDescription = (description: string): string => {
    // Extract number from description
    const match = description.match(/Momentos Marcantes Image (\d+)/);
    if (match) {
      return `momentos_${match[1]}`;
    }

    // Default to converting description to key format
    return description.toLowerCase().replace(/\s+/g, '_');
  };

  const loadExternalImages = async () => {
    setLoadingImages(true);
    try {
      // Get latest external images
      const images = await scanExternalImageUrls();
      setExternalImages(images);
    } catch (error) {
      console.error('Error loading external images:', error);
      toast.error('Erro ao carregar imagens externas');
    } finally {
      setLoadingImages(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const report = await generateImageReport();
      const blob = new Blob([report], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'external-images-report.md';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Erro ao gerar relatório');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-8 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-8">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-8 space-y-4"
        >
          <Link
            to="/cms"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-primary transition-colors w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao CMS
          </Link>
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Configurações</h1>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-[200px,1fr] gap-8">
          <nav className="space-y-2 sticky top-32">
            {categories.map((category, index) => (
              <motion.button
                key={category.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActiveCategory(category.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeCategory === category.id
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                    : 'hover:bg-secondary/80 hover:shadow-md'
                }`}
              >
                <category.icon className="w-5 h-5" />
                {category.label}
                {activeCategory === category.id && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute inset-0 bg-primary/10 rounded-lg -z-10"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </nav>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6 bg-card/20 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
          >
            {activeCategory === 'appearance' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-4">Tema</label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'light', label: 'Claro', icon: Sun },
                      { id: 'dark', label: 'Escuro', icon: Moon },
                      { id: 'system', label: 'Sistema', icon: Laptop }
                    ].map(theme => (
                      <motion.button
                        key={theme.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSettingChange('appearance', 'theme', theme.id)}
                        className={`p-6 rounded-xl flex flex-col items-center gap-3 transition-all ${
                          settings.appearance.theme === theme.id
                            ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                            : 'bg-secondary/50 hover:bg-secondary/80'
                        }`}
                      >
                        <theme.icon className="w-8 h-8" />
                        <span>{theme.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <label className="text-sm font-medium">Animações</label>
                  </div>
                  <button
                    onClick={() => handleSettingChange('appearance', 'animations', !settings.appearance.animations)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.appearance.animations ? 'bg-primary' : 'bg-secondary'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.appearance.animations ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">CSS Personalizado</label>
                  <div className="relative">
                    <Code className="absolute left-3 top-3 w-5 h-5 text-primary/60" />
                    <textarea
                      value={settings.appearance.customCss}
                      onChange={(e) => handleSettingChange('appearance', 'customCss', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none font-mono text-sm"
                      rows={4}
                      placeholder=":root { --custom-color: #ff0000; }"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeCategory === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-4">Frequência de Notificações</label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'realtime', label: 'Tempo Real', icon: Zap },
                      { id: 'hourly', label: 'Por Hora', icon: Clock },
                      { id: 'daily', label: 'Diário', icon: Calendar }
                    ].map(freq => (
                      <motion.button
                        key={freq.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSettingChange('notifications', 'frequency', freq.id)}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 transition-all ${
                          settings.notifications.frequency === freq.id
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary/50 hover:bg-secondary/80'
                        }`}
                      >
                        <freq.icon className="w-6 h-6" />
                        <span>{freq.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="bg-secondary/30 rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BellOff className="w-5 h-5 text-primary" />
                      <label className="text-sm font-medium">Modo Não Perturbe</label>
                    </div>
                    <button
                      onClick={() => handleSettingChange('notifications', 'quietHours', {
                        ...settings.notifications.quietHours,
                        enabled: !settings.notifications.quietHours.enabled
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.notifications.quietHours.enabled ? 'bg-primary' : 'bg-secondary'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.notifications.quietHours.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {settings.notifications.quietHours.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Início</label>
                        <input
                          type="time"
                          value={settings.notifications.quietHours.start}
                          onChange={(e) => handleSettingChange('notifications', 'quietHours', {
                            ...settings.notifications.quietHours,
                            start: e.target.value
                          })}
                          className="w-full px-4 py-2 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Fim</label>
                        <input
                          type="time"
                          value={settings.notifications.quietHours.end}
                          onChange={(e) => handleSettingChange('notifications', 'quietHours', {
                            ...settings.notifications.quietHours,
                            end: e.target.value
                          })}
                          className="w-full px-4 py-2 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeCategory === 'security' && (
              <div className="space-y-6">
                <div className="bg-secondary/30 rounded-xl p-6 space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                    Requisitos de Senha
                  </h3>
                  <div className="space-y-3">
                    {[
                      { id: 'uppercase', label: 'Letras Maiúsculas', icon: 'A' },
                      { id: 'numbers', label: 'Números', icon: '123' },
                      { id: 'symbols', label: 'Símbolos', icon: '@#$' }
                    ].map(req => (
                      <div key={req.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 flex items-center justify-center bg-primary/20 rounded-lg text-primary font-mono">
                            {req.icon}
                          </span>
                          <span className="text-sm">{req.label}</span>
                        </div>
                        <button
                          onClick={() => handleSettingChange('security', 'passwordRequirements', {
                            ...settings.security.passwordRequirements,
                            [req.id]: !settings.security.passwordRequirements[req.id]
                          })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.security.passwordRequirements[req.id] ? 'bg-primary' : 'bg-secondary'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              settings.security.passwordRequirements[req.id] ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeCategory === 'backup' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: 'local', label: 'Local', icon: HardDrive },
                    { id: 'cloud', label: 'Cloud', icon: Cloud }
                  ].map(location => (
                    <motion.button
                      key={location.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        const locations = settings.backup.backupLocations;
                        const newLocations = locations.includes(location.id)
                          ? locations.filter(l => l !== location.id)
                          : [...locations, location.id];
                        handleSettingChange('backup', 'backupLocations', newLocations);
                      }}
                      className={`p-6 rounded-xl flex flex-col items-center gap-3 transition-all ${
                        settings.backup.backupLocations.includes(location.id)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary/50 hover:bg-secondary/80'
                      }`}
                    >
                      <location.icon className="w-8 h-8" />
                      <span>{location.label}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {activeCategory === 'analytics' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Caminhos Excluídos</label>
                  <div className="flex flex-wrap gap-2">
                    {settings.analytics.excludedPaths.map((path, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-1 bg-secondary/50 rounded-full"
                      >
                        <Ban className="w-4 h-4" />
                        <span className="text-sm">{path}</span>
                        <button
                          onClick={() => {
                            const newPaths = settings.analytics.excludedPaths.filter((_, i) => i !== index);
                            handleSettingChange('analytics', 'excludedPaths', newPaths);
                          }}
                          className="p-1 hover:bg-secondary/80 rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const path = prompt('Digite o caminho a ser excluído (ex: /admin/*)');
                        if (path) {
                          handleSettingChange('analytics', 'excludedPaths', [
                            ...settings.analytics.excludedPaths,
                            path
                          ]);
                        }
                      }}
                      className="px-3 py-1 bg-secondary/50 hover:bg-secondary/80 rounded-full text-sm"
                    >
                      + Adicionar
                    </button>
                  </div>
                </div>
              </div>
            )}

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="sticky bottom-0 pt-6 border-t border-border/10 bg-background/80 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowResetConfirm(true)}
                  className="px-6 py-3 bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20 transition-colors"
                >
                  Restaurar Padrões
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : showSuccess ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {saving ? 'Salvando...' : showSuccess ? 'Salvo!' : 'Salvar Alterações'}
                </motion.button>
              </div>
            </motion.div>

            <AnimatePresence>
              {showResetConfirm && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-card rounded-lg p-6 max-w-md w-full mx-4"
                  >
                    <h3 className="text-xl font-bold mb-4">Restaurar Configurações</h3>
                    <p className="text-gray-400 mb-6">
                      Tem certeza que deseja restaurar todas as configurações para os valores padrão? Esta ação não pode ser desfeita.
                    </p>
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setShowResetConfirm(false)}
                        className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => {
                          handleReset();
                          setShowResetConfirm(false);
                        }}
                        className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
                      >
                        Confirmar
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {showImageBrowser && (
          <ImageBrowser
            onSelect={async (url) => {
              // Get the correct key based on the selected image description
              const key = selectedImageKey || getImageKeyFromDescription(selectedImage?.description || '');
              
              if (key && settings) {
                const newSettings = { ...settings };
                // Initialize media and imageUrls if they don't exist
                if (!newSettings.media) {
                  newSettings.media = {
                    maxUploadSize: 50,
                    allowedFileTypes: ['.jpg', '.png', '.gif', '.mp3', '.mp4'],
                    imageCompression: true,
                    defaultImageQuality: 80,
                    autoOptimize: true,
                    storageQuota: 5120,
                    imageUrls: {}
                  };
                }
                if (!newSettings.media.imageUrls) {
                  newSettings.media.imageUrls = {};
                }

                newSettings.media.imageUrls[key] = url;

                console.log('Updating settings with new image URL:', {
                  key,
                  newUrl: url,
                  newSettings
                });
                // Update the URL for the specific key
                newSettings.media.imageUrls[key] = url;

                await updateSettings(newSettings);
                // Reload data
                await loadExternalImages();
                toast.success('Imagem atualizada com sucesso!');
                setSelectedImageKey('');
                setSelectedImage(null);
              }
              setShowImageBrowser(false);
            }}
            onClose={() => setShowImageBrowser(false)}
            isAdmin={true}
          />
        )}

        {activeCategory === 'media' && (
          <div className="space-y-6">
            <div className="bg-secondary/30 rounded-xl p-6 relative">
              <h3 className="text-lg font-medium flex items-center gap-2 mb-6">
                <FileImage className="w-5 h-5 text-primary" />
                Imagens Externas
                <button
                  onClick={handleGenerateReport}
                  className="ml-auto px-4 py-2 bg-primary/20 text-primary rounded-lg text-sm hover:bg-primary/30 transition-colors"
                >
                  Gerar Relatório
                </button>
              </h3>
              
              {loadingImages ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : externalImages.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <UploadCloud className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma imagem externa encontrada.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {externalImages.map((image, index) => (
                    <div key={index} className="bg-secondary/20 rounded-lg p-4 hover:bg-secondary/30 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="w-24 h-24 rounded-lg overflow-hidden bg-secondary/30">
                          <img 
                            src={image.url} 
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://placehold.co/96x96?text=Error';
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-sm truncate max-w-[200px]">
                              {image.description}
                            </h4>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const key = getImageKeyFromDescription(image.description);
                                  setSelectedImageKey(key);
                                  setSelectedImage(image);
                                  setSelectedImage(image);
                                  setShowImageBrowser(true);
                                }}
                                className="px-3 py-1 bg-primary/20 text-primary rounded-lg text-sm hover:bg-primary/30 transition-colors"
                              >
                                Alterar Imagem
                              </button>
                              <button
                                onClick={() => setShowImageBrowser(true)}
                                className="p-2 bg-secondary/50 hover:bg-secondary/80 rounded-lg transition-colors"
                                title="Abrir Galeria"
                              >
                                <Image className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="text-sm text-gray-400">
                            <p>Localização: {image.location}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;